/**
 * Box Primitive - FluentUI Design Kit
 */

import React from 'react';
import DOMPurify from 'dompurify';
import { RadiusSize, RadiusToken, SpacingSize, SpacingToken } from './helpers/boxHelper';
import type { BoxProps } from './Box.types';
import { useFluentTokens } from '@themes/Provider';

/**
 * Box - A utility component for layout and styling with Design Kit tokens
 *
 * Makes it easy to use Design Kit tokens without remembering exact token names.
 * Supports refs for DOM access (scrolling, focus management, etc.)
 *
 * @example
 * <Box p="m" bg="surface1" borderRadius="medium">
 *   <Box color="text1">Content with medium padding, surface background</Box>
 * </Box>
 *
 * @example
 * const scrollRef = useRef<HTMLDivElement>(null);
 * <Box ref={scrollRef} style={{ overflowY: 'auto' }}>
 *   Scrollable content
 * </Box>
 */
export const Box = React.forwardRef<HTMLElement, BoxProps>(
  (
    {
      children,
      as = 'div',
      // Spacing props
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
      // Color props
      bg,
      color,
      // Border radius
      borderRadius,
      // Layout props
      display,
      flexDirection,
      alignItems,
      justifyContent,
      gap,
      // Size props
      width,
      height,
      maxWidth,
      maxHeight,
      // Additional props
      style = {},
      className,
      htmlContent,
      onClick,
      onMouseEnter,
      onMouseLeave,
      ...rest
    },
    ref,
  ) => {
    const { tokens: designKitTokens, modeTokens } = useFluentTokens();

    // Helper to convert spacing size to Design Kit token value
    const getSpacing = (size: SpacingSize): string => {
      const tokenMap: Record<SpacingSize, SpacingToken> = {
        xxs: 'spacingVerticalXXS',
        xs: 'spacingVerticalXS',
        s: 'spacingVerticalS',
        m: 'spacingVerticalM',
        l: 'spacingVerticalL',
        xl: 'spacingVerticalXL',
        xxl: 'spacingVerticalXXL',
      };
      return designKitTokens[tokenMap[size]];
    };

    // Helper to convert background color shorthand to Design Kit token
    const getBackgroundColor = (bgColor: NonNullable<BoxProps['bg']>): string => {
      const colorMap = {
        surface1: modeTokens.colorNeutralBackground1,
        surface2: modeTokens.colorNeutralBackground2,
        surface3: modeTokens.colorNeutralBackground3,
        brand1: modeTokens.colorBrand80,
        brand2: modeTokens.colorBrand90,
      };
      return colorMap[bgColor];
    };

    // Helper to convert text color shorthand to Design Kit token
    const getTextColor = (textColor: NonNullable<BoxProps['color']>): string => {
      const colorMap = {
        text1: modeTokens.colorNeutralForeground1,
        text2: modeTokens.colorNeutralForeground2,
        brand1: modeTokens.colorBrand80,
        brand2: modeTokens.colorBrand90,
      };
      return colorMap[textColor];
    };

    // Helper to convert border radius to Design Kit token
    const getBorderRadius = (radius: RadiusSize): string => {
      const radiusMap: Record<RadiusSize, RadiusToken> = {
        small: 'borderRadiusSmall',
        medium: 'borderRadiusMedium',
        large: 'borderRadiusLarge',
        xlarge: 'borderRadiusXLarge',
        xxlarge: 'borderRadiusXXLarge',
        xxxlarge: 'borderRadiusXXXLarge',
        xxxxlarge: 'borderRadiusXXXXLarge',
      };
      return designKitTokens[radiusMap[radius]];
    };

    // Build styles using Design Kit tokens
    const boxStyles: React.CSSProperties = {
      // Spacing styles
      ...(p && { padding: getSpacing(p) }),
      ...(px && { paddingLeft: getSpacing(px), paddingRight: getSpacing(px) }),
      ...(py && { paddingTop: getSpacing(py), paddingBottom: getSpacing(py) }),
      ...(pt && { paddingTop: getSpacing(pt) }),
      ...(pr && { paddingRight: getSpacing(pr) }),
      ...(pb && { paddingBottom: getSpacing(pb) }),
      ...(pl && { paddingLeft: getSpacing(pl) }),

      ...(m && { margin: getSpacing(m) }),
      ...(mx && { marginLeft: getSpacing(mx), marginRight: getSpacing(mx) }),
      ...(my && { marginTop: getSpacing(my), marginBottom: getSpacing(my) }),
      ...(mt && { marginTop: getSpacing(mt) }),
      ...(mr && { marginRight: getSpacing(mr) }),
      ...(mb && { marginBottom: getSpacing(mb) }),
      ...(ml && { marginLeft: getSpacing(ml) }),

      // Color styles
      ...(bg && { backgroundColor: getBackgroundColor(bg) }),
      ...(color && { color: getTextColor(color) }),

      // Border radius
      ...(borderRadius && { borderRadius: getBorderRadius(borderRadius) }),

      // Layout styles
      ...(display && { display }),
      ...(flexDirection && { flexDirection }),
      ...(alignItems && { alignItems }),
      ...(justifyContent && { justifyContent }),
      ...(gap && { gap: getSpacing(gap) }),

      // Size styles
      ...(width && { width }),
      ...(height && { height }),
      ...(maxWidth && { maxWidth }),
      ...(maxHeight && { maxHeight }),

      // Merge with custom styles
      ...style,
    };

    // If htmlContent is provided, sanitize and render as HTML
    const content = htmlContent
      ? React.createElement('div', {
          dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(htmlContent) },
        })
      : children;

    return React.createElement(
      as,
      {
        ref,
        className,
        style: boxStyles,
        onClick,
        onMouseEnter,
        onMouseLeave,
        ...rest,
      },
      content,
    );
  },
);

// Add display name for better debugging
Box.displayName = 'Box';
